import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Truck, Menu, X, User, LogOut, LayoutDashboard, Settings as SettingsIcon, MessageSquare, Users, HelpCircle, ShieldCheck, Bell, Briefcase, AtSign, CheckCheck, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mediaUrl } from '../utils/media';
import { useCommunityUnread } from '../hooks/useCommunityUnread';
import api from '../utils/api';

const Navbar = ({ verificationMode = false }) => {
  const { user, logout } = useAuth();
  const { count: communityUnread } = useCommunityUnread();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const navigate = useNavigate();
  const notifBtnRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const res = await api.get('/profile/notifications');
        setNotifCount(res.data.unread_count || 0);
        setNotifs(res.data.notifications?.slice(0, 8) || []);
      } catch { /* silent */ }
      try {
        const inb = await api.get('/inbox/unread-count');
        setInboxUnread(inb.data.unread_count || 0);
      } catch { /* silent */ }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // Click outside to close dropdown
  useEffect(() => {
    if (!showNotifs) return;
    const onClick = (e) => {
      if (notifBtnRef.current && !notifBtnRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showNotifs]);

  const markRead = async (id) => {
    try {
      await api.put(`/profile/notifications/${id}/read`);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setNotifCount(c => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/profile/notifications/read-all');
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setNotifCount(0);
    } catch { /* silent */ }
  };

  const handleNotifClick = async (n) => {
    if (!n.read) markRead(n.id);
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };

  const notifTimeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    if (diff < 604800) return `${Math.floor(diff/86400)}d`;
    return new Date(iso).toLocaleDateString();
  };

  const handleLogout = () => { logout(); };

  // In verification mode, only show logo + sign out
  if (verificationMode) {
    return (
      <nav className="bg-slate-900/80 backdrop-blur-md sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" alt="StraightConnect Logo" className="h-10 w-auto" />
              <span className="font-heading text-2xl font-bold text-white tracking-tight">StraightConnect</span>
            </div>
            <span className="text-sm font-medium text-slate-300">StraightConnect</span>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white rounded-lg transition-colors" data-testid="verification-signout-btn">
              <LogOut className="h-4 w-4" /><span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="glassmorphism sticky top-0 z-50 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG" 
              alt="StraightConnect Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
              StraightConnect
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {!user && (
              <>
                <Link to="/resource-hub" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-resource-hub-link">Resources</Link>
                <Link to="/login" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-login-link">Login</Link>
                <Link to="/join" className="bg-slate-900 text-white px-6 py-2 rounded-md font-semibold hover:bg-slate-800 transition-all btn-hover-lift" data-testid="nav-join-btn">Join Now</Link>
              </>
            )}
            {user && user.is_admin && (
              <>
                <Link to="/admin" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-admin-link">Admin Dashboard</Link>
                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-slate-200">
                  <Link to="/inbox" className="relative text-slate-700 hover:text-blue-500 transition-colors" title="Inbox" data-testid="nav-inbox-link">
                    <Mail className="h-5 w-5" />
                    {inboxUnread > 0 && <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{inboxUnread > 9 ? '9+' : inboxUnread}</span>}
                  </Link>
                  <div className="relative" ref={notifBtnRef}>
                    <button onClick={() => setShowNotifs(!showNotifs)} className="text-slate-700 hover:text-blue-500 transition-colors relative" data-testid="nav-notifications-btn">
                      <Bell className="h-5 w-5" />
                      {notifCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{notifCount > 9 ? '9+' : notifCount}</span>}
                    </button>
                    <AnimatePresence>
                      {showNotifs && (
                        <motion.div
                          initial={{ opacity: 0, y: -8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.97 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 top-9 w-[360px] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden"
                          data-testid="notif-dropdown"
                        >
                          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900 text-sm">Notifications</span>
                              {notifCount > 0 && <span className="inline-flex items-center px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-bold">{notifCount} new</span>}
                            </div>
                            {notifCount > 0 && (
                              <button onClick={markAllRead} className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-semibold" data-testid="mark-all-read-btn">
                                <CheckCheck className="h-3 w-3" /> Mark all read
                              </button>
                            )}
                          </div>
                          <div className="max-h-[420px] overflow-y-auto">
                            {notifs.length === 0 ? (
                              <div className="py-10 text-center">
                                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                                <p className="text-sm text-slate-400">You&apos;re all caught up</p>
                              </div>
                            ) : notifs.map(n => {
                              const isMention = n.type?.startsWith('mention');
                              return (
                                <button
                                  key={n.id}
                                  onClick={() => handleNotifClick(n)}
                                  className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-blue-50/40' : ''}`}
                                  data-testid={`notif-item-${n.id}`}
                                >
                                  <div className="relative flex-shrink-0">
                                    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                                      {n.actor_picture ? <img src={mediaUrl(n.actor_picture)} alt="" className="w-full h-full object-cover" /> : <User className="h-4 w-4 text-slate-500" />}
                                    </div>
                                    {isMention && (
                                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-sky-500 border-2 border-white flex items-center justify-center">
                                        <AtSign className="h-2 w-2 text-white" strokeWidth={3} />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-700'} truncate`}>{n.title || 'Notification'}</p>
                                      <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">{notifTimeAgo(n.created_at)}</span>
                                    </div>
                                    {n.message && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>}
                                  </div>
                                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-3" />}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <Link to="/settings" className="text-slate-700 hover:text-blue-500 transition-colors" data-testid="nav-settings-link"><SettingsIcon className="h-5 w-5" /></Link>
                  <span className="flex items-center gap-2 text-slate-700 font-medium" data-testid="nav-admin-name">
                    <span className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {user.profile_picture ? <img src={mediaUrl(user.profile_picture)} alt="" className="w-full h-full object-cover" /> : <User className="h-4 w-4 text-slate-500" />}
                    </span>
                    {user.name}
                  </span>
                  <button onClick={handleLogout} className="flex items-center gap-2 text-slate-700 hover:text-red-500 transition-colors" data-testid="nav-logout-btn"><LogOut className="h-5 w-5" /></button>
                </div>
              </>
            )}
            {user && !user.is_admin && (
              <>
                {user.role !== 'learner' && user.role !== 'affiliate' && user.role !== 'service_provider' && (
                  <>
                    <Link to="/dashboard" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-dashboard-link">Dashboard</Link>
                    <Link to="/jobs" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-jobs-link">Jobs</Link>
                    <Link to="/resources" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-resources-link">Resources</Link>
                    <Link to="/community" className="relative text-slate-700 hover:text-blue-500 font-medium transition-colors inline-flex items-center gap-1.5" data-testid="nav-community-link">
                      Community
                      {communityUnread > 0 && (
                        <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center" data-testid="nav-community-badge">
                          {communityUnread > 99 ? '99+' : communityUnread}
                        </span>
                      )}
                    </Link>
                    <Link to="/members" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-members-link">Members</Link>
                    <Link to="/referrals" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-referrals-link">Referrals</Link>
                    {user.role === 'carrier_owner' && (
                      <Link to="/compliance" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-compliance-link">Compliance</Link>
                    )}
                    <Link to="/help-center" className="text-slate-700 hover:text-blue-500 font-medium transition-colors" data-testid="nav-help-center-link">Help</Link>
                  </>
                )}
                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-slate-200">
                  <Link to="/inbox" className="relative text-slate-700 hover:text-blue-500 transition-colors" title="Inbox" data-testid="nav-inbox-link">
                    <Mail className="h-5 w-5" />
                    {inboxUnread > 0 && <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{inboxUnread > 9 ? '9+' : inboxUnread}</span>}
                  </Link>
                  <div className="relative" ref={notifBtnRef}>
                    <button onClick={() => setShowNotifs(!showNotifs)} className="text-slate-700 hover:text-blue-500 transition-colors relative" data-testid="nav-notifications-btn">
                      <Bell className="h-5 w-5" />
                      {notifCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{notifCount > 9 ? '9+' : notifCount}</span>}
                    </button>
                    <AnimatePresence>
                      {showNotifs && (
                        <motion.div
                          initial={{ opacity: 0, y: -8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.97 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 top-9 w-[360px] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden"
                          data-testid="notif-dropdown"
                        >
                          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900 text-sm">Notifications</span>
                              {notifCount > 0 && <span className="inline-flex items-center px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-bold">{notifCount} new</span>}
                            </div>
                            {notifCount > 0 && (
                              <button onClick={markAllRead} className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-semibold" data-testid="mark-all-read-btn">
                                <CheckCheck className="h-3 w-3" /> Mark all read
                              </button>
                            )}
                          </div>
                          <div className="max-h-[420px] overflow-y-auto">
                            {notifs.length === 0 ? (
                              <div className="py-10 text-center">
                                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                                <p className="text-sm text-slate-400">You&apos;re all caught up</p>
                              </div>
                            ) : notifs.map(n => {
                              const isMention = n.type?.startsWith('mention');
                              return (
                                <button
                                  key={n.id}
                                  onClick={() => handleNotifClick(n)}
                                  className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-blue-50/40' : ''}`}
                                  data-testid={`notif-item-${n.id}`}
                                >
                                  <div className="relative flex-shrink-0">
                                    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                                      {n.actor_picture ? (
                                        <img src={mediaUrl(n.actor_picture)} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <User className="h-4 w-4 text-slate-500" />
                                      )}
                                    </div>
                                    {isMention && (
                                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-sky-500 border-2 border-white flex items-center justify-center">
                                        <AtSign className="h-2 w-2 text-white" strokeWidth={3} />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-slate-900' : 'text-slate-700'} truncate`}>{n.title || 'Notification'}</p>
                                      <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">{notifTimeAgo(n.created_at)}</span>
                                    </div>
                                    {n.message && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>}
                                  </div>
                                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-3" />}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <Link to="/settings" className="text-slate-700 hover:text-blue-500 transition-colors" data-testid="nav-settings-link"><SettingsIcon className="h-5 w-5" /></Link>
                  {user.role === 'service_provider' && (
                    <Link to="/profile" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border border-sky-200 text-sky-700 rounded-md text-sm font-semibold hover:bg-sky-100 transition-colors" data-testid="nav-sp-home-link">
                      <Briefcase className="h-4 w-4" /> SP Home
                    </Link>
                  )}
                  <Link to="/profile" className="flex items-center gap-2 text-slate-700 hover:text-blue-500 transition-colors" data-testid="nav-profile-link">
                    <span className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {user.profile_picture ? <img src={mediaUrl(user.profile_picture)} alt="" className="w-full h-full object-cover" /> : <User className="h-4 w-4 text-slate-500" />}
                    </span>
                    <span className="font-medium">{user.name}</span>
                  </Link>
                  <button onClick={handleLogout} className="flex items-center gap-2 text-slate-700 hover:text-red-500 transition-colors" data-testid="nav-logout-btn"><LogOut className="h-5 w-5" /></button>
                </div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-slate-700 hover:text-blue-500"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-200 bg-white"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-3">
              {!user && (
                <>
                  <Link to="/resource-hub" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-resource-hub-link">Resources</Link>
                  <Link to="/login" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-login-link">Login</Link>
                  <Link to="/join" className="block px-4 py-2 bg-slate-900 text-white text-center rounded-md font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-join-btn">Join Now</Link>
                </>
              )}
              {user && user.is_admin && (
                <>
                  <Link to="/admin" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-admin-link">
                    <LayoutDashboard className="inline h-5 w-5 mr-2" />Admin Dashboard
                  </Link>
                  <Link to="/inbox" className="flex items-center px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-inbox-link-admin">
                    <Mail className="inline h-5 w-5 mr-2" />Inbox
                    {inboxUnread > 0 && <span className="ml-auto bg-blue-600 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-bold">{inboxUnread > 9 ? '9+' : inboxUnread}</span>}
                  </Link>
                  <Link to="/settings" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-settings-link">
                    <SettingsIcon className="inline h-5 w-5 mr-2" />Settings
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-md" data-testid="mobile-admin-logout-btn">
                    <LogOut className="inline h-5 w-5 mr-2" />Logout
                  </button>
                </>
              )}
              {user && !user.is_admin && (
                <>
                  {user.role !== 'learner' && user.role !== 'affiliate' && user.role !== 'service_provider' && (
                    <>
                      <Link to="/dashboard" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-dashboard-link">
                        <LayoutDashboard className="inline h-5 w-5 mr-2" />Dashboard
                      </Link>
                      <Link to="/jobs" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-jobs-link">Jobs</Link>
                      <Link to="/resources" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-resources-link">Resources</Link>
                      <Link to="/community" className="relative block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-community-link">
                        <MessageSquare className="inline h-5 w-5 mr-2" />Community
                        {communityUnread > 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-orange-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center" data-testid="mobile-community-badge">
                            {communityUnread > 99 ? '99+' : communityUnread}
                          </span>
                        )}
                      </Link>
                      <Link to="/members" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-members-link">
                        <Users className="inline h-5 w-5 mr-2" />Members
                      </Link>
                      <Link to="/referrals" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-referrals-link">Referrals</Link>
                      {user.role === 'carrier_owner' && (
                        <Link to="/compliance" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-compliance-link">
                          <ShieldCheck className="inline h-5 w-5 mr-2" />Compliance
                        </Link>
                      )}
                      <Link to="/help-center" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-help-center-link">
                        <HelpCircle className="inline h-5 w-5 mr-2" />Help Center
                      </Link>
                    </>
                  )}
                  <Link to="/inbox" className="flex items-center px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-inbox-link">
                    <Mail className="inline h-5 w-5 mr-2" />Inbox
                    {inboxUnread > 0 && <span className="ml-auto bg-blue-600 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-bold">{inboxUnread > 9 ? '9+' : inboxUnread}</span>}
                  </Link>
                  <Link to="/settings" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-settings-link">
                    <SettingsIcon className="inline h-5 w-5 mr-2" />Settings
                  </Link>
                  {user.role === 'service_provider' && (
                    <Link to="/profile" className="block px-4 py-2 text-sky-700 bg-sky-50 hover:bg-sky-100 rounded-md font-semibold" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-sp-home-link">
                      <Briefcase className="inline h-5 w-5 mr-2" />SP Home
                    </Link>
                  )}
                  <Link to="/profile" className="block px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-profile-link">
                    <User className="inline h-5 w-5 mr-2" />Profile
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-md" data-testid="mobile-logout-btn">
                    <LogOut className="inline h-5 w-5 mr-2" />Logout
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;